/**
 * Fetched at runtime from /config.json, not baked in at build time. The
 * real Cognito User Pool/Client ID and API endpoint only exist after
 * deploy, and the same built JS bundle gets promoted from beta to
 * production unchanged (architecture §11a) -- so per-environment values
 * can't be compiled in. FrontendStack deploys config.json alongside the
 * built app, with its actual values resolved by CloudFormation at deploy
 * time from the real Auth/Api stack outputs in that account.
 */
export interface RuntimeConfig {
  cognitoUserPoolId: string;
  cognitoUserPoolClientId: string;
  apiBaseUrl: string;
  webSocketUrl: string;
  /**
   * The same limit S3's presigned POST policy enforces, so the upload form can refuse an oversized file
   * up front instead of after transferring it. Optional because a distribution deployed before this
   * field existed still serves a config.json without it -- callers fall back rather than crashing.
   */
  maxUploadContentLengthBytes?: number;
}

let cached: RuntimeConfig | null = null;

export async function loadConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  const response = await fetch("/config.json");
  if (!response.ok) {
    throw new Error(`Failed to load /config.json: ${response.status}`);
  }
  cached = (await response.json()) as RuntimeConfig;
  return cached;
}

export function getConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error("Config not loaded yet -- call loadConfig() before using getConfig().");
  }
  return cached;
}

/**
 * The same config, but null instead of a throw when it isn't loaded.
 *
 * `getConfig` throwing is right for the things the app cannot function without (the API base URL, the
 * user pool) -- failing loudly beats a page that half-works. It is wrong for an optional nicety like the
 * upload size hint: a component reading that during render would take the whole page down over a value
 * it has a perfectly good default for. Use this for anything with a sane fallback.
 */
export function getConfigOrNull(): RuntimeConfig | null {
  return cached;
}
